import axios from "axios";
import { API_BASE_URL, attachApiErrorHandler } from "./authService";
import type { BusinessTypeKey } from "../platform/config";

/**
 * The platform (superadmin) token and an organization token are signed with the
 * same secret but are firewalled from each other: each is rejected on the
 * other's routes. So the panel gets its own axios instance, its own storage
 * keys, and — critically — never sends `x-organization-id`, because there is no
 * organization context on `/superadmin/**`.
 */
export const SUPERADMIN_TOKEN_KEY = "sa_api_token";
export const SUPERADMIN_REFRESH_KEY = "sa_refresh_token";
export const SUPERADMIN_ADMIN_KEY = "sa_admin";

export const superadminClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

export const getSuperadminToken = (): string | null =>
  localStorage.getItem(SUPERADMIN_TOKEN_KEY);

export const getSuperadminRefreshToken = (): string | null =>
  localStorage.getItem(SUPERADMIN_REFRESH_KEY);

export const setSuperadminTokens = (
  accessToken: string,
  refreshToken: string
): void => {
  localStorage.setItem(SUPERADMIN_TOKEN_KEY, accessToken);
  localStorage.setItem(SUPERADMIN_REFRESH_KEY, refreshToken);
};

export const clearSuperadminSession = (): void => {
  localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
  localStorage.removeItem(SUPERADMIN_REFRESH_KEY);
  localStorage.removeItem(SUPERADMIN_ADMIN_KEY);
};

export const isSuperadminAuthenticated = (): boolean => !!getSuperadminToken();

superadminClient.interceptors.request.use(
  (config: any) => {
    const token = getSuperadminToken();
    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Never attach x-organization-id here — the panel has no org context.
    delete config.headers["x-organization-id"];
    return config;
  },
  (error) => Promise.reject(error)
);

let isRedirectingToLogin = false;

const redirectToLogin = (): void => {
  clearSuperadminSession();
  if (typeof window === "undefined") return;
  if (window.location?.pathname === "/login") return;
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  window.location.replace("/login");
};

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token!);
  });
  failedQueue = [];
};

superadminClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config as
      | { headers?: Record<string, any>; _retry?: boolean; url?: string }
      | undefined;

    const isAuthCall =
      typeof originalRequest?.url === "string" &&
      /\/auth\/superadmin\/(request-otp|verify-otp|refresh)/.test(
        originalRequest.url
      );

    if (
      !originalRequest ||
      error?.response?.status !== 401 ||
      originalRequest._retry ||
      isAuthCall
    ) {
      return Promise.reject(error);
    }

    const refreshToken = getSuperadminRefreshToken();
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${token}`,
            };
            resolve(superadminClient(originalRequest as any));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const res = await axios.post<SuperadminTokens>(
        `${API_BASE_URL}/auth/superadmin/refresh`,
        { refreshToken }
      );
      const { accessToken, refreshToken: newRefresh } = res.data;
      setSuperadminTokens(accessToken, newRefresh);
      processQueue(null, accessToken);
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${accessToken}`,
      };
      return superadminClient(originalRequest as any);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed too — the admin has to go back through the OTP.
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

attachApiErrorHandler(superadminClient);

/* ------------------------------------------------------------------ types */

/** Keys known today. `GET /superadmin/features` is the canonical list. */
export type FeatureKey = "LOYALTY" | "CLASSES" | "STOCK" | string;

/** All keys are always present — you never receive a partial map. */
export type FeatureMap = Record<string, boolean>;

export interface SuperadminTokens {
  accessToken: string;
  refreshToken: string;
}

export interface SuperadminAdmin {
  id: string;
  phone: string;
  country?: string;
  fullName: string | null;
  lastLoginAt?: string | null;
}

export interface OrganizationUsage {
  businesses: number;
  resources: number;
  staff: number;
  clients: number;
  bookingsTotal: number;
  bookingsInWindow: number;
  /** null means the organization has never taken a booking. */
  lastBookingAt: string | null;
}

/** Which backoffice the tenant signed up through. */
export type PlatformKey = "SPORTS" | "CLINIC";

export const PLATFORM_KEYS: PlatformKey[] = ["SPORTS", "CLINIC"];

export type SubscriptionStatus =
  | "UNLIMITED"
  | "TRIAL"
  | "ACTIVE"
  | "EXPIRING"
  /** Past the end date, inside the grace days before the lock. */
  | "GRACE"
  | "EXPIRED";

export const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  "TRIAL",
  "ACTIVE",
  "EXPIRING",
  "GRACE",
  "EXPIRED",
  "UNLIMITED",
];

export interface SubscriptionSummary {
  status: SubscriptionStatus;
  /** null when unlimited; 0 once expired. */
  daysLeft: number | null;
  /** null = unlimited. */
  endsAt: string | null;
  isTrial: boolean;
  plan: { id: string; name: string } | SubscriptionPlan | null;
}

export interface OrganizationRow {
  id: string;
  name: string;
  country: string;
  timezone: string;
  createdAt: string;
  isDeleted: boolean;
  /** Gates the unauthenticated `/public/*` catalogue. Not part of `features`. */
  publicBrowseEnabled: boolean;
  platform: PlatformKey;
  /** Self-serve only: clinic, spa, salon, beauty or neutral. */
  businessType?: BusinessTypeKey | null;
  /** Whole days since the organization signed up. */
  ageDays: number;
  subscription: SubscriptionSummary;
  features: FeatureMap;
  usage: OrganizationUsage;
}

export interface OrganizationsPage {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  /** The window `usage.bookingsInWindow` is counted over. */
  windowDays: number;
  organizations: OrganizationRow[];
}

export interface OrganizationStats {
  organization: {
    id: string;
    name: string;
    country: string;
    timezone: string;
    createdAt: string;
    isDeleted: boolean;
    billingTenantId?: string | null;
    loyaltyTenantId?: string | null;
    publicBrowseEnabled: boolean;
    platform: PlatformKey;
    businessType?: BusinessTypeKey | null;
    ageDays: number;
    subscription: SubscriptionSummary;
  };
  features: FeatureMap;
  windowDays: number;
  since: string;
  setup: {
    businesses: number;
    units: number;
    resources: number;
    products: number;
  };
  people: { staff: number; clients: number; coaches: number };
  bookings: {
    total: number;
    inWindow: number;
    upcoming: number;
    /** Keyed by status name; the set of keys varies, so iterate it. */
    byStatusInWindow: Record<string, number>;
    lastCreatedAt: string | null;
  };
  classes: { offerings: number; enrollments: number };
  packages: { offered: number; sold: number };
  stock: { movementsInWindow: number };
  notifications: { sentThisMonth: number };
}

export interface OrganizationFeatures {
  organizationId: string;
  features: FeatureMap;
}

export interface OrganizationPublicBrowse {
  organizationId: string;
  publicBrowseEnabled: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  durationDays: number;
  /** null = offered on every platform. */
  platform: PlatformKey | null;
  features: string[];
  isActive: boolean;
  sortOrder: number;
}

export type PlanInput = Partial<Omit<SubscriptionPlan, "id">>;

export interface PlatformSettings {
  /** null = signups start unlimited; 0 = they start locked until activated. */
  trialDays: Record<PlatformKey, number | null>;
  /** Digits with country code, used for the owners' "Contact us" link. */
  salesWhatsappNumber: string | null;
}

export interface SubscriptionHistoryRow {
  id: string;
  source: "TRIAL" | "MANUAL" | "WHISH" | string;
  plan: { id: string; name: string } | null;
  startsAt: string;
  endsAt: string | null;
  amount: number | null;
  currency: string | null;
  note: string | null;
  /** Phone of the superadmin; null for the signup trial. */
  createdBy: string | null;
  createdAt: string;
}

/** One Whish payment for a package; also the tenant's invoice. */
export interface SubscriptionPaymentRow {
  id: string;
  invoiceNumber: number;
  organizationId: string;
  planId: string | null;
  planName: string;
  durationDays: number;
  amount: number;
  currency: string;
  status: "PENDING" | "PAID" | "FAILED";
  provider: string;
  periodStartsAt: string | null;
  periodEndsAt: string | null;
  paidAt: string | null;
  failureReason: string | null;
  createdAt: string;
  /**
   * Who the invoice is for. `deleted` once the organization is soft- or
   * hard-deleted, and then `name` already reads "<name> (Deleted)"; a
   * hard-deleted organization has no id left.
   */
  organization?: { id: string | null; name: string; platform: PlatformKey; deleted: boolean };
}

export interface PaymentsSummary {
  /** "demo" until the Whish credentials are set on the server. */
  mode: "live" | "demo";
  pendingCount: number;
  /** Per currency; USD and LBP are never added together. */
  totals: Array<{
    currency: string;
    thisMonth: number;
    last12Months: number;
    count: number;
  }>;
  /** Last 12 months, oldest first; one key per currency. */
  revenueByMonth: Array<{ month: string } & Record<string, number | string>>;
  recent: SubscriptionPaymentRow[];
}

export interface PaymentsPage {
  page: number;
  pageSize: number;
  total: number;
  payments: SubscriptionPaymentRow[];
}

export const listSubscriptionPayments = async (params: {
  status?: SubscriptionPaymentRow["status"];
  platform?: PlatformKey;
  organizationId?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaymentsPage> => {
  const { data } = await superadminClient.get<PaymentsPage>("/superadmin/payments", {
    params,
  });
  return data;
};

export interface OrganizationSubscription {
  current: SubscriptionSummary & {
    platform: PlatformKey;
    organizationCreatedAt: string;
  };
  history: SubscriptionHistoryRow[];
  /** The tenant's Whish payments (invoices), newest first. */
  payments: SubscriptionPaymentRow[];
}

/**
 * One action on a tenant's subscription. The backend decides the new end date
 * from the first of: `endsAt` (null = unlimited), `extendDays`, the plan's
 * duration. `planId` omitted keeps the current plan; null clears it.
 */
export interface SubscriptionChange {
  planId?: string | null;
  endsAt?: string | null;
  extendDays?: number;
  isTrial?: boolean;
  amount?: number;
  currency?: string;
  note?: string;
}

export type StatusCounts = Record<SubscriptionStatus, number> & {
  total: number;
};

export interface OverviewOrgRow {
  id: string;
  name: string;
  platform: PlatformKey;
  status: SubscriptionStatus;
  daysLeft: number | null;
  endsAt: string | null;
  plan: { id: string; name: string } | null;
}

export interface PlatformOverview {
  generatedAt: string;
  expiringWindowDays: number;
  byPlatform: Record<PlatformKey, StatusCounts>;
  /** Last 12 months, oldest first, keyed YYYY-MM. */
  signupsByMonth: Array<{ month: string } & Record<PlatformKey, number>>;
  endingSoon: OverviewOrgRow[];
  recentlyExpired: OverviewOrgRow[];
  payments: PaymentsSummary;
}

/* --------------------------------------------------------------- auth flow */

export interface RequestOtpResponse {
  message?: string;
  channel?: "WHATSAPP" | "SMS" | string;
  expiresInMinutes?: number;
}

/** Resend. Each call replaces the previous code. 5 requests / 5 minutes. */
export const requestSuperadminOtp = async (
  phone: string,
  country?: string
): Promise<RequestOtpResponse> => {
  const { data } = await superadminClient.post<RequestOtpResponse>(
    "/auth/superadmin/request-otp",
    country ? { phone, country } : { phone },
    // The OTP screen renders its own message (429 needs its own copy).
    { suppressToast: true } as any
  );
  return data;
};

export interface VerifyOtpResponse extends SuperadminTokens {
  message: string;
  admin: SuperadminAdmin;
}

/** Six digits, valid 10 minutes, single use. 10 requests / 5 minutes. */
export const verifySuperadminOtp = async (
  phone: string,
  code: string,
  country?: string
): Promise<VerifyOtpResponse> => {
  const { data } = await superadminClient.post<VerifyOtpResponse>(
    "/auth/superadmin/verify-otp",
    country ? { phone, code, country } : { phone, code },
    // Shown inline under the code field instead.
    { suppressToast: true } as any
  );
  setSuperadminTokens(data.accessToken, data.refreshToken);
  localStorage.setItem(SUPERADMIN_ADMIN_KEY, JSON.stringify(data.admin));
  return data;
};

export const getSuperadminMe = async (): Promise<SuperadminAdmin> => {
  const { data } = await superadminClient.get<SuperadminAdmin>(
    "/auth/superadmin/me"
  );
  localStorage.setItem(SUPERADMIN_ADMIN_KEY, JSON.stringify(data));
  return data;
};

export const getCachedSuperadmin = (): SuperadminAdmin | null => {
  try {
    const raw = localStorage.getItem(SUPERADMIN_ADMIN_KEY);
    return raw ? (JSON.parse(raw) as SuperadminAdmin) : null;
  } catch {
    return null;
  }
};

/** Invalidates every token issued to this admin, on every device. */
export const superadminLogout = async (): Promise<void> => {
  try {
    if (getSuperadminToken()) {
      await superadminClient.post("/auth/superadmin/logout", {});
    }
  } catch {
    // The session is going away either way.
  } finally {
    clearSuperadminSession();
    if (typeof window !== "undefined") window.location.replace("/login");
  }
};

/* ------------------------------------------------------------------ panel */

export const listOrganizations = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
  platform?: PlatformKey;
  subscription?: SubscriptionStatus;
  hideDeleted?: boolean;
}): Promise<OrganizationsPage> => {
  const { data } = await superadminClient.get<OrganizationsPage>(
    "/superadmin/organizations",
    {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
        ...(params.search ? { search: params.search } : {}),
        ...(params.platform ? { platform: params.platform } : {}),
        ...(params.subscription ? { subscription: params.subscription } : {}),
        ...(params.hideDeleted ? { hideDeleted: true } : {}),
      },
    }
  );
  return data;
};

/** `days` is the rolling window, 1–365, default 30. */
export const getOrganizationStats = async (
  organizationId: string,
  days?: number
): Promise<OrganizationStats> => {
  const { data } = await superadminClient.get<OrganizationStats>(
    `/superadmin/organizations/${organizationId}/stats`,
    { params: days ? { days } : undefined }
  );
  return data;
};

/** The canonical key list and column order — read it, don't hardcode. */
export const getFeatureKeys = async (): Promise<string[]> => {
  const { data } = await superadminClient.get<{ features: string[] }>(
    "/superadmin/features"
  );
  return Array.isArray(data?.features) ? data.features : [];
};

export const getOrganizationFeatures = async (
  organizationId: string
): Promise<OrganizationFeatures> => {
  const { data } = await superadminClient.get<OrganizationFeatures>(
    `/superadmin/organizations/${organizationId}/features`
  );
  return data;
};

/**
 * Send one toggle or several. The response always carries the complete
 * resulting map, so reconcile row state from it rather than from the
 * optimistic guess.
 */
export const updateOrganizationFeatures = async (
  organizationId: string,
  features: { feature: string; enabled: boolean }[]
): Promise<OrganizationFeatures> => {
  const { data } = await superadminClient.patch<OrganizationFeatures>(
    `/superadmin/organizations/${organizationId}/features`,
    { features },
    // The caller rolls the switch back and toasts with its own copy.
    { suppressToast: true } as any
  );
  return data;
};

/**
 * A column on the organization, not a feature key. Server-side it applies at
 * once, but `/public/*` responses are cached for up to 5 minutes, so visitors
 * who already loaded the catalogue can keep reading it briefly after turning it off.
 */
export const setOrganizationPublicBrowse = async (
  organizationId: string,
  enabled: boolean
): Promise<OrganizationPublicBrowse> => {
  const { data } = await superadminClient.patch<OrganizationPublicBrowse>(
    `/superadmin/organizations/${organizationId}/public-browse`,
    { enabled },
    // The caller rolls the switch back and toasts with its own copy.
    { suppressToast: true } as any
  );
  return data;
};

/** For a tenant that signed up through the wrong link. */
export const setOrganizationPlatform = async (
  organizationId: string,
  platform: PlatformKey
): Promise<{ organizationId: string; platform: PlatformKey }> => {
  const { data } = await superadminClient.patch<{
    organizationId: string;
    platform: PlatformKey;
  }>(
    `/superadmin/organizations/${organizationId}/platform`,
    { platform }
  );
  return data;
};

/* ----------------------------------------------------------- subscriptions */

export const getPlatformOverview = async (): Promise<PlatformOverview> => {
  const { data } = await superadminClient.get<PlatformOverview>(
    "/superadmin/overview"
  );
  return data;
};

export const listPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data } = await superadminClient.get<{ plans: SubscriptionPlan[] }>(
    "/superadmin/plans"
  );
  return Array.isArray(data?.plans) ? data.plans : [];
};

export const createPlan = async (
  plan: PlanInput
): Promise<SubscriptionPlan> => {
  const { data } = await superadminClient.post<SubscriptionPlan>(
    "/superadmin/plans",
    plan
  );
  return data;
};

/** Plans are archived with `isActive: false`, never deleted. */
export const updatePlan = async (
  planId: string,
  plan: PlanInput
): Promise<SubscriptionPlan> => {
  const { data } = await superadminClient.patch<SubscriptionPlan>(
    `/superadmin/plans/${planId}`,
    plan
  );
  return data;
};

export const getPlatformSettings = async (): Promise<PlatformSettings> => {
  const { data } = await superadminClient.get<PlatformSettings>(
    "/superadmin/settings"
  );
  return data;
};

export const updatePlatformSettings = async (
  patch: Partial<{
    trialDays: Partial<Record<PlatformKey, number | null>>;
    salesWhatsappNumber: string | null;
  }>
): Promise<PlatformSettings> => {
  const { data } = await superadminClient.patch<PlatformSettings>(
    "/superadmin/settings",
    patch
  );
  return data;
};

export const getOrganizationSubscription = async (
  organizationId: string
): Promise<OrganizationSubscription> => {
  const { data } = await superadminClient.get<OrganizationSubscription>(
    `/superadmin/organizations/${organizationId}/subscription`
  );
  return data;
};

export const changeOrganizationSubscription = async (
  organizationId: string,
  change: SubscriptionChange
): Promise<OrganizationSubscription> => {
  const { data } = await superadminClient.patch<OrganizationSubscription>(
    `/superadmin/organizations/${organizationId}/subscription`,
    change
  );
  return data;
};

/* ----------------------------------------------------------- presentation */

export const PLATFORM_LABELS: Record<PlatformKey, string> = {
  SPORTS: "Sports",
  CLINIC: "Appointments",
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  UNLIMITED: "Unlimited",
  TRIAL: "Trial",
  ACTIVE: "Active",
  EXPIRING: "Expiring",
  GRACE: "Unpaid",
  EXPIRED: "Expired",
};

/** Text on a tinted background; each pair clears 4.5:1. */
export const SUBSCRIPTION_STATUS_COLORS: Record<
  SubscriptionStatus,
  { bg: string; fg: string }
> = {
  UNLIMITED: { bg: "#EEF0F3", fg: "#3F4753" },
  TRIAL: { bg: "#E6F0FB", fg: "#1F5A96" },
  ACTIVE: { bg: "#E3F4EA", fg: "#17663F" },
  EXPIRING: { bg: "#FDF1DC", fg: "#8A5A00" },
  GRACE: { bg: "#FCE8D8", fg: "#8F3B00" },
  EXPIRED: { bg: "#FBE5E3", fg: "#A32B22" },
};

/** Display names live in the frontend — the API returns keys only. */
export const FEATURE_LABELS: Record<string, string> = {
  LOYALTY: "Loyalty",
  CLASSES: "Classes",
  STOCK: "Stock",
};

export const FEATURE_DESCRIPTIONS: Record<string, string> = {
  LOYALTY: "Points, rewards and loyalty tiers.",
  CLASSES: "Classes, coaches and packages.",
  STOCK: "Inventory, products and stock movements.",
};

export const featureLabel = (key: string): string =>
  FEATURE_LABELS[key] ||
  key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, " ");

/* ---------------------------------------------------------- deleting orgs */

/** Soft delete (true) locks everyone out and can be undone (false). */
export const setOrganizationDeleted = async (
  organizationId: string,
  deleted: boolean
): Promise<{ id: string; name: string; isDeleted: boolean }> => {
  const { data } = await superadminClient.patch<{ id: string; name: string; isDeleted: boolean }>(
    `/superadmin/organizations/${organizationId}/deleted`,
    { deleted }
  );
  return data;
};

export interface DeletionPreview {
  organization: { id: string; name: string; isDeleted: boolean };
  /** Rows a hard delete would remove, biggest tables first. */
  tables: { table: string; rows: number }[];
  totalRows: number;
  /** Invoices that are kept (not in `tables`), shown as "<name> (Deleted)". */
  keptInvoices: number;
  /** Why a hard delete is refused right now; empty when it is allowed. */
  blockers: string[];
}

export const getDeletionPreview = async (organizationId: string): Promise<DeletionPreview> => {
  const { data } = await superadminClient.get<DeletionPreview>(
    `/superadmin/organizations/${organizationId}/deletion-preview`
  );
  return data;
};

/** Permanent. The backend requires the exact organization name. */
export const hardDeleteOrganization = async (
  organizationId: string,
  confirmName: string
): Promise<{ totalRows: number; deleted: Record<string, number>; keptInvoices: number }> => {
  // A DELETE with a body: sent through request() so the body is typed.
  const { data } = await superadminClient.request<{
    totalRows: number;
    deleted: Record<string, number>;
    keptInvoices: number;
  }>({
    method: "DELETE",
    url: `/superadmin/organizations/${organizationId}`,
    data: { confirmName },
  } as any);
  return data;
};
